/**
 * Хранит пользовательский выбор записи и восстанавливает его при открытии
 * страницы. Подходит для экранов с несколькими кабинетами, автопарками и
 * другими переключаемыми сущностями.
 *
 * Страница передаёт ID из URL, сохранённый ID и свежий список из backend;
 * утилита выбирает только существующую запись.
 *
 * Пример:
 * const selectedId = selectAvailableResourceId({
 *   routeResourceId: route.query.resource_id,
 *   savedResourceId: getStoredSelectedResourceId("page.selectedResourceId"),
 *   resources,
 * });
 */

/**
 * Возвращает сохранённый ID строкой или null, если выбора нет либо localStorage
 * недоступен. Приведение к типу предметного ID остаётся за доменной обёрткой.
 */
export function getStoredSelectedResourceId(storageKey) {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/**
 * Сохраняет ID выбранной записи. Пустое значение очищает выбор, чтобы при
 * следующем открытии страницы не восстановить неактуальную запись.
 */
export function setStoredSelectedResourceId(storageKey, resourceId) {
  try {
    if (resourceId) {
      localStorage.setItem(storageKey, String(resourceId));
      return;
    }

    localStorage.removeItem(storageKey);
  } catch {
    // localStorage может быть недоступен в приватном режиме.
  }
}

/**
 * Проверяет, есть ли ID среди записей, полученных от backend. getResourceId
 * нужен, когда ID лежит не в поле id, например в option.value у BaseSelect.
 * Сравнение не зависит от того, пришёл ID строкой или числом.
 */
export function selectedResourceExists(
  resourceId,
  resources = [],
  getResourceId = (resource) => resource.id
) {
  return resources.some(
    (resource) => String(getResourceId(resource)) === String(resourceId)
  );
}

/**
 * Ссылка на конкретную запись важнее сохранённого выбора. Оба значения
 * используем только после проверки по свежему списку из backend. fallbackId
 * нужен для экранов, которые при первом входе должны открыть первую запись.
 */
export function selectAvailableResourceId({
  routeResourceId = null,
  savedResourceId = null,
  resources = [],
  getResourceId,
  fallbackId = null,
}) {
  const resourceIds = [routeResourceId, savedResourceId];

  return (
    resourceIds.find((resourceId) =>
      selectedResourceExists(resourceId, resources, getResourceId)
    ) || fallbackId
  );
}

/**
 * Выбор карточки в списке с модалкой: URL имеет приоритет, закрытие модалки
 * сохраняет текущий выбор (в том числе вне страницы), новая выборка выбирает
 * первую запись. Отсутствующую в списке запись загружает и проверяет store.
 */
export function selectWorkspaceResourceId({ routeResourceId, selectedId, resources, listChanged }) {
  return routeResourceId || (!listChanged && selectedId) || resources[0]?.id || null;
}
